import { Image, type ImageViewerEntry } from '../../../../src/apps/runtime/kit/index.ts';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appMediaCopy from '@/i18n/ns/app-media';
import { useAppCatalogLocale } from '@/i18n/context';
import type { ManagedImageData } from './media-model';
import type { RenderImage } from './media-parts';

/**
 * Binds the tabs' injected `renderImage` to APP's public Image (never a copy of it): ART's
 * `ManagedImageData` maps `ref` to `image`, `ratio` to `imageRatio`, `fit` to `imageFit`, with
 * `cropSafe` and `caption` passed through. Zoom is the host's `useImageViewer`: `openImage` is its
 * `open(key, origin)`; without one, no zoom trigger is drawn. The App kit styles are the host's.
 */
export function useManagedImageRenderer(
  openImage?: (key: string, origin: HTMLElement) => void,
  /** Inspect on: a click selects the image and never zooms, so no zoom trigger is drawn. */
  inspectOn = false,
): RenderImage {
  const copy = useNamespaceCopy(appMediaCopy);
  const locale = useAppCatalogLocale();
  const day = new Intl.DateTimeFormat(locale ?? undefined, { dateStyle: 'medium' });
  const imageCopy = {
    capturing: copy.imageCapturing,
    missing: copy.imageMissing,
    unavailable: copy.imageUnavailable,
    refused: copy.imageRefused,
    loadFailed: copy.imageRefused,
    open: (alt: string) => formatCopy(copy.imageOpen, { alt }),
    inspect: (alt: string) => formatCopy(copy.imageInspect, { alt }),
    seen: (observedAt: string) => {
      const at = new Date(observedAt);
      return formatCopy(copy.imageSeen, {
        date: Number.isNaN(at.getTime()) ? observedAt : day.format(at),
      });
    },
  };
  return (data, size, owner) => (
    <Image
      image={data.ref}
      imageRatio={data.ratio}
      imageFit={data.fit}
      cropSafe={data.cropSafe}
      caption={data.caption}
      size={size}
      copy={imageCopy}
      inspectOn={inspectOn}
      onOpen={
        openImage ? (_image, origin) => openImage(viewerKey(owner, data.key), origin) : undefined
      }
    />
  );
}

/** The viewer's item identity: where the image is drawn plus ART's key. */
export const viewerKey = (owner: string, key: string) => `${owner}\u0000${key}`;

/** The entries for APP's `useImageViewer`: one per drawn image, with its own projection. */
export function viewerEntries(
  images: readonly { owner: string; data: ManagedImageData }[],
): ImageViewerEntry[] {
  return images.map(({ owner, data }) => ({
    key: viewerKey(owner, data.key),
    image: data.ref,
    ratio: data.ratio,
    changedSince: data.changedSince,
  }));
}
